/**
 * 极简 IndexedDB 封装（无第三方依赖），供本地模式存储使用。
 * 对象存储：groups（key=id）、items（key=id, index=date）、meta（key=key）。
 */
const DB_NAME = 'daynote-local';
const DB_VERSION = 1;

export type StoreName = 'groups' | 'items' | 'meta';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('groups')) {
        db.createObjectStore('groups', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('items')) {
        const items = db.createObjectStore('items', { keyPath: 'id' });
        items.createIndex('date', 'date', { unique: false });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  run: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(store, mode);
        const req = run(transaction.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

export const idb = {
  getAll<T>(store: StoreName): Promise<T[]> {
    return tx<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);
  },
  get<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
    return tx<T | undefined>(store, 'readonly', (s) => s.get(key) as IDBRequest<T | undefined>);
  },
  put<T>(store: StoreName, value: T): Promise<IDBValidKey> {
    return tx<IDBValidKey>(store, 'readwrite', (s) => s.put(value as unknown as object));
  },
  delete(store: StoreName, key: IDBValidKey): Promise<undefined> {
    return tx<undefined>(store, 'readwrite', (s) => s.delete(key) as IDBRequest<undefined>);
  },
  /** 按 date 区间取 items（闭区间，字符串比较即可，因 YYYY-MM-DD 字典序=时间序） */
  getItemsByDateRange<T>(start: string, end: string): Promise<T[]> {
    return tx<T[]>('items', 'readonly', (s) => {
      const index = s.index('date');
      const range = IDBKeyRange.bound(start, end);
      return index.getAll(range) as IDBRequest<T[]>;
    });
  },
};

/** 请求持久化存储，降低浏览器在空间紧张时回收 IndexedDB 的概率 */
export async function requestPersistentStorage(): Promise<void> {
  try {
    if (navigator.storage && navigator.storage.persist) {
      await navigator.storage.persist();
    }
  } catch {
    /* 忽略：非关键能力 */
  }
}
